import React from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import PlaySmallIcon from '~/assets/icons/PlaySmallIcon';
import CardDurationInfo from '~/components/Card/components/CardDurationInfo/CardDurationInfo';
import IKImage from '~/components/IKImage/IKImage';
import ToggleFromList from '~/components/ToggleFromList/ToggleFromList';
import CategoryLabel from '~/components/UI/CategoryLabel/CategoryLabel';
import DifficultyIcon from '~/components/UI/DifficultyIcon/DifficultyIcon';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { buildExpertRoute } from '~/routes';
import { CssUtilityType } from '~/styled';
import { BREAKPOINTS_VALUES, SMALL_MARGIN_PX } from '~/theme';
import { VideoCategory, VideoDifficulty } from '~/types';
import { secondsToHms } from '~/utils/dateUtils/secondsToHms';
import { createCategoryLink } from '~/utils/urlUtils/createCategoryLink';

interface SideVideoCardProps {
  id?: string | null;
  title?: string | null;
  author?: string | null;
  duration?: number | null;
  previewUrl?: string | null;
  videoUrl?: string | null;
  categories?: (VideoCategory | null)[] | null;
  link: string;
  difficulty?: VideoDifficulty | null;
  authorSlug?: string | null;
  wrapperClass?: string;
  wrapperCss?: CssUtilityType;
  isInWatchlist: boolean;
}

const SideVideoCard: React.FC<SideVideoCardProps> = ({
  id,
  title,
  author,
  duration,
  previewUrl,
  categories,
  link,
  difficulty,
  authorSlug,
  wrapperClass,
  wrapperCss,
  isInWatchlist,
  ...rest
}) => {
  const theme = useTheme();
  const { userInfo } = useUserInfoContext();

  const category = categories && categories.length > 0 ? categories[0] : null;
  const categoryLink = createCategoryLink(category);
  const expertLink = buildExpertRoute(authorSlug);

  return (
    <CardWrapper data-testid={rest['data-testid']} optionalStyles={wrapperCss}>
      <Link to={link}>
        <ImageWrapper>
          {previewUrl && (
            <>
              <StyledIKImage
                path={previewUrl}
                alt={title}
                draggable={false}
                srcset={[{ width: 420 }, { width: 370 }, { width: 350 }, { width: 340 }]}
                sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 420px, (max-width: ${BREAKPOINTS_VALUES['md']}) 370px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
              />
              <PlayBtnWrapper>
                <StyledPlayIcon color={theme.palette.common.white} />
              </PlayBtnWrapper>
            </>
          )}
        </ImageWrapper>
      </Link>
      {category && (
        <CategoryWrapper>
          <StyledCategoryLabel name={category.title} url={categoryLink} />
          {!!difficulty && <DifficultyIcon difficulty={difficulty} />}
        </CategoryWrapper>
      )}
      <TextWrapper>
        <Link to={link}>
          <Title>{title}</Title>
        </Link>
        <InfoRow>
          {(author || duration) && (
            <CardDurationInfo
              durationName={author}
              duration={secondsToHms(duration)}
              link={expertLink}
            />
          )}
          {userInfo && <ToggleFromList id={id} existInWatchlist={isInWatchlist} />}
        </InfoRow>
      </TextWrapper>
    </CardWrapper>
  );
};

export default SideVideoCard;

const CardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles?: CssUtilityType }>`
  border-radius: 10px;
  background: ${({ theme }) => theme.palette.secondary.light};
  padding-bottom: 24px;
  max-width: 343px;
  min-width: 288px;
  width: 100%;
  margin-bottom: ${SMALL_MARGIN_PX};
  height: 250px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 318px;
    padding-bottom: 32px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const ImageWrapper = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 179px;
  overflow: hidden;
  display: flex;

  &::after {
    cursor: pointer;
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    height: 136px;
    width: 100%;
    z-index: 2;
    background: linear-gradient(
      0deg,
      ${({ theme }) => theme.palette.common.blackSecondary} 0%,
      ${({ theme }) => alpha(theme.palette.common.blackSecondary, 0.9)} 17.24%,
      ${({ theme }) => alpha(theme.palette.common.blackSecondary, 0.0001)} 100%
    );
  }
`;
const StyledIKImage = styled(IKImage)`
  cursor: pointer;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px 10px 0 0;
`;
const PlayBtnWrapper = styled(Box)`
  cursor: pointer;
  position: absolute;
  transform: translate(-50%, -60%);
  background: ${({ theme }) => alpha(theme.palette.common.blackSecondary, 0.5)};
  top: 50%;
  left: 50%;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 50px;
  height: 50px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 70px;
    height: 70px;
  }
`;
const StyledPlayIcon = styled(PlaySmallIcon)`
  width: 24px;
  height: 28px;
  margin-left: 2px;
  transition: all 0.3s;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 35px;
    height: 40px;
    margin-left: 4px;
  }
`;
const CategoryWrapper = styled('div')`
  display: flex;
  padding: 0 24px;
  margin-bottom: 12px;
  z-index: 2;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 32px;
  }

  & > div:nth-child(2) {
    margin-left: 10px;
  }
`;
const StyledCategoryLabel = styled(CategoryLabel)`
  max-width: 200px;
  z-index: 3;
`;
const TextWrapper = styled(Box)`
  padding: 0px 24px;
  z-index: 2;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 32px;
  }
`;
const Title = styled(Typography)`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.2;
  color: ${({ theme }) => theme.palette.common.white};
  overflow: hidden;
  white-space: inherit;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;

  && {
    margin-bottom: 12px;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 20px;
    line-height: 28px;
  }
`;
const InfoRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
